import { formatCompact, formatDate, formatNumber, formatPercent } from '../lib/format.ts';

export interface BarDatum {
  label: string;
  value: number;
  highlight?: boolean;
  note?: string;
}

/**
 * Barras horizontais: a comparação aqui é entre itens nomeados (contas,
 * publicações), e rótulo à esquerda lê melhor que eixo x rotacionado.
 */
export function BarChart({
  data,
  unit = '',
  maxBars = 12,
}: {
  data: BarDatum[];
  unit?: string;
  maxBars?: number;
}) {
  const rows = data.slice(0, maxBars);
  const max = Math.max(...rows.map((d) => d.value), 1);

  return (
    <div className="bar-chart">
      {rows.map((row) => (
        <div className="bar-chart__row" key={row.label}>
          <span className="bar-chart__label" title={row.label}>
            {row.label}
          </span>
          <span className="bar-chart__track">
            <span
              className={`bar-chart__fill${row.highlight ? ' bar-chart__fill--highlight' : ''}`}
              style={{ width: `${Math.max((row.value / max) * 100, 0.6)}%` }}
            />
          </span>
          <span className="bar-chart__value tabular-nums">
            {formatNumber(row.value)}
            {unit}
          </span>
        </div>
      ))}
    </div>
  );
}

export interface SeriesPoint {
  date: string;
  value: number;
}

/** Linha simples para séries datadas. Com um ponto só, vira um marcador. */
export function LineChart({
  points,
  height = 180,
  label,
  format,
  tickFormat,
}: {
  points: SeriesPoint[];
  height?: number;
  label?: string;
  /** Como escrever um valor na dica de cada ponto (padrão: inteiro). */
  format?: (value: number) => string;
  /** Como escrever os rótulos do eixo, que são estreitos (padrão: número compacto). */
  tickFormat?: (value: number) => string;
}) {
  if (points.length === 0) return null;

  const width = 640;
  const padding = { top: 16, right: 16, bottom: 28, left: 64 };
  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || Math.abs(max) || 1;
  const yMin = min - span * 0.1;
  const yMax = max + span * 0.1;

  const x = (i: number) => (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW);
  const y = (v: number) => innerH - ((v - yMin) / (yMax - yMin)) * innerH;

  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.value)}`).join(' ');

  return (
    <svg
      className="line-chart"
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label={label ?? 'Série temporal'}
      preserveAspectRatio="none"
    >
      <g transform={`translate(${padding.left},${padding.top})`}>
        {[0, 0.5, 1].map((t) => (
          <g key={t}>
            <line
              x1={0}
              x2={innerW}
              y1={innerH * t}
              y2={innerH * t}
              stroke="var(--chart-grid)"
              strokeWidth={1}
            />
            <text
              x={-8}
              y={innerH * t + 4}
              textAnchor="end"
              fill="var(--chart-tick-label)"
              fontSize={11}
            >
              {(tickFormat ?? formatCompact)(yMax - (yMax - yMin) * t)}
            </text>
          </g>
        ))}

        {points.length > 1 && (
          <path d={path} fill="none" stroke="var(--chart-series-1)" strokeWidth={2} />
        )}
        {points.map((p, i) => (
          <circle key={p.date} cx={x(i)} cy={y(p.value)} r={3.5} fill="var(--chart-series-1)">
            <title>{`${formatDate(p.date)}: ${format ? format(p.value) : formatNumber(p.value)}`}</title>
          </circle>
        ))}

        <text x={0} y={innerH + 20} fill="var(--chart-tick-label)" fontSize={11}>
          {formatDate(points[0]?.date)}
        </text>
        {points.length > 1 && (
          <text
            x={innerW}
            y={innerH + 20}
            textAnchor="end"
            fill="var(--chart-tick-label)"
            fontSize={11}
          >
            {formatDate(points[points.length - 1]?.date)}
          </text>
        )}
      </g>
    </svg>
  );
}

/**
 * Mini-gráfico de linha para uma série curta dentro de uma tabela (uma rodada por ponto). Só a forma:
 * os valores exatos ficam nas colunas e no histórico. Com menos de dois pontos não há o que mostrar.
 */
export function Sparkline({ values, label }: { values: number[]; label: string }) {
  if (values.length < 2) return null;
  const width = 84;
  const height = 24;
  const pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i: number) => pad + (i / (values.length - 1)) * (width - pad * 2);
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);
  const path = values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const last = values.length - 1;
  return (
    <svg className="sparkline" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={path} fill="none" stroke="var(--chart-series-1)" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(last)} cy={y(values[last]!)} r={2.5} fill="var(--chart-series-1)" />
    </svg>
  );
}

export interface DistributionSlice {
  label: string;
  percent: number;
}

/** Barra única de 100% — a leitura aqui é "partes de um todo", não ranking. */
export function DistributionBar({ slices }: { slices: DistributionSlice[] }) {
  const palette = [
    'var(--chart-series-1)',
    'var(--chart-series-2)',
    'var(--chart-series-3)',
    'var(--chart-series-4)',
    'var(--chart-series-5)',
  ];

  return (
    <div className="distribution">
      <div className="distribution__bar">
        {slices.map((slice, i) => (
          <span
            key={slice.label}
            className="distribution__segment"
            style={{
              width: `${slice.percent}%`,
              background: palette[i % palette.length],
            }}
            title={`${slice.label}: ${formatPercent(slice.percent)}`}
          />
        ))}
      </div>
      <ul className="distribution__legend">
        {slices.map((slice, i) => (
          <li key={slice.label}>
            <span
              className="distribution__swatch"
              style={{ background: palette[i % palette.length] }}
              aria-hidden="true"
            />
            <span className="text-label">{slice.label}</span>
            <span className="text-muted text-label tabular-nums">{formatPercent(slice.percent)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
